import { useMemo, useRef } from "react";
import type { ReactNode } from "react";
import Table, { type ColumnSpec } from "views/merchandise/tables/BulkActionsTable";
import { formatCurrency, formatNumber, formatPercent } from "utils/formatNumber";
import { pathLabelMapping } from "./utils";
import { Badge } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import PathToPurchaseExpander from "./PathToPurchaseExpander";
import ProgressBar from "components/core/basic/ProgressBar";
import type { RowInfo } from "react-table-6";
import { type ConversionPathData } from "./types";
import useAds from "hooks/useAds";

interface Props {
  tableData: ConversionPathData[];
  scopeDescription: ReactNode;
  campaignReportId?: number;
}

interface MetricLimits {
  [key: string]: number;
  path_occurrences: number;
  impressions: number;
  total_cost: number;
  users_that_purchased: number;
  sales_amount: number;
  purchases: number;
  user_purchase_rate: number;
  ntb_users_that_purchased: number;
  ntb_sales_amount: number;
  ntb_purchases: number;
  ntb_percentage: number;
}

interface MediaPlanMetadata {
  id: number;
  manual_campaign_name: string;
  auto_campaign_name: string;
  contextual_campaign_name: string;
  audience_campaign_name: string;
  name: string;
}

export interface Campaign {
  id: number;
  name: string;
}

interface ExpandedContentProps {
  row?: RowInfo;
  campaignReportId?: number;
}

const PathToPurchaseTable = ({ tableData, scopeDescription, campaignReportId }: Props) => {
  const TABLE_ID = "userConversionPathTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const tableRef = useRef(null);

  const { ads: advertisements, isLoading: isAdDataLoading } = useAds();
  const createCampaignLookup = (
    advertisements: MediaPlanMetadata[],
    key: keyof MediaPlanMetadata,
  ): { [key: string]: Campaign } => {
    return advertisements.reduce(
      (acc: { [x: string]: Campaign }, advertisement: MediaPlanMetadata) => {
        acc[advertisement[key] as string] = { name: advertisement.name, id: advertisement.id };
        return acc;
      },
      {} as { [key: string]: Campaign },
    );
  };

  const formattedTableData = useMemo(() => {
    return tableData.map((row) => {
      if (!row.sales_amount) {
        return row;
      }
      const acos = row.total_cost / row.sales_amount;
      return { ...row, acos };
    });
  }, [tableData]);

  const manualCampaignLookup: { [key: string]: Campaign } = useMemo(
    () => createCampaignLookup(advertisements ?? [], "manual_campaign_name"),
    [advertisements],
  );

  const autoCampaignLookup: { [key: string]: Campaign } = useMemo(
    () => createCampaignLookup(advertisements ?? [], "auto_campaign_name"),
    [advertisements],
  );

  const contextualCampaignLookup: { [key: string]: Campaign } = useMemo(
    () => createCampaignLookup(advertisements ?? [], "contextual_campaign_name"),
    [advertisements],
  );

  const audienceCampaignLookup: { [key: string]: Campaign } = useMemo(
    () => createCampaignLookup(advertisements ?? [], "audience_campaign_name"),
    [advertisements],
  );

  const metricLimits = useMemo(() => {
    return tableData.reduce((acc: MetricLimits, row) => {
      for (const key in row) {
        const value = row[key];

        if (typeof value === "string" && isNaN(parseFloat(value))) {
          continue;
        }

        if (typeof value === "number") {
          acc[key] = Math.max(acc[key] ?? -Infinity, value);
        } else if (typeof value === "string") {
          const parsedValue = parseFloat(value);
          if (!isNaN(parsedValue)) {
            acc[key] = Math.max(acc[key] ?? -Infinity, parsedValue);
          }
        }
      }
      return acc;
    }, {} as MetricLimits);
  }, [tableData]);

  const columnSpecs: ColumnSpec[] = useMemo(
    () => [
      {
        key: "path_occurrences",
        options: {
          columnTitle: "Path Occurences",
          format: "number",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.path_occurrences) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "impressions",
        options: {
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.impressions) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "cost",
        options: {
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA", true)}
                <ProgressBar progress={(v / metricLimits.total_cost) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "users_that_purchased",
        options: {
          columnTitle: "Users That Purchased",
          format: "number",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.users_that_purchased) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "sales_amount",
        options: {
          columnTitle: "Sales Amount",
          format: "currency",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA", true)}
                <ProgressBar progress={(v / metricLimits.sales_amount) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "purchases",
        options: {
          columnTitle: "Purchases",
          format: "number",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.purchases) * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "acos",
        options: {
          columnTitle: "ACOS",
          format: "percent",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v)}
                <ProgressBar progress={v * 100} width={65} />
              </span>
            );
          },
        },
      },
      {
        key: "user_purchase_rate",
        options: {
          columnTitle: "User Purchase Rate",
          format: "percent",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v, 1)}
                <ProgressBar progress={(v / metricLimits.user_purchase_rate) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_users_that_purchased",
        options: {
          columnTitle: "NTB Users That Purchased",
          format: "number",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {v}
                <ProgressBar progress={(v / metricLimits.ntb_users_that_purchased) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_sales_amount",
        options: {
          columnTitle: "NTB Sales Amount",
          format: "currency",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatCurrency(v, "USA", true)}
                <ProgressBar progress={v / metricLimits.ntb_sales_amount} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_purchases",
        options: {
          columnTitle: "NTB Purchases",
          format: "number",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatNumber(v)}
                <ProgressBar progress={(v / metricLimits.ntb_purchases) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
      {
        key: "ntb_percentage",
        options: {
          columnTitle: "NTB Percentage",
          format: "percent",
          formatter: (v: number): ReactNode => {
            return (
              <span className="d-flex align-items-center gap-3">
                {formatPercent(v * 100, 1)}
                <ProgressBar progress={(v / metricLimits.ntb_percentage) * 100} width={65} />
              </span>
            );
          },
          checked: false,
        },
      },
    ],
    [metricLimits],
  );

  return (
    <Table
      tableRef={tableRef}
      tableId={TABLE_ID}
      isExpandable={true}
      isSelectable={false}
      titleCol={{
        id: "path",
        Header: "Path",
        accessor: "text",
        style: { width: "450px" },
        width: 450,
        Cell: (row: { original: { path: string } }) => {
          const { path } = row.original;

          const pathArray = path.match(/[A-Z]+-[A-Za-z]+/g)?.map((touchpoint: string) => {
            const key = touchpoint.split("-")[0]!;
            return pathLabelMapping[key as keyof object];
          });

          return (
            <div style={{ display: "flex", fontWeight: "500", fontSize: "1.5rem" }}>
              {pathArray?.map(
                (
                  touchpoint: {
                    opacity: number;
                    label: ReactNode;
                  },
                  index,
                ) => (
                  <div className="d-flex align-items-center" key={index}>
                    <Badge className={`badge bg-trellis-purple`} style={{ opacity: touchpoint.opacity }}>
                      {touchpoint.label}
                    </Badge>{" "}
                    {index < pathArray.length - 1 ?
                      <FaArrowRight className="mx-2" />
                    : null}
                  </div>
                ),
              )}
            </div>
          );
        },
      }}
      data={formattedTableData}
      columnSpecs={columnSpecs}
      excludedFilterColumns={["conversion_type", "path"]}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getSearchCriteria={(row: any) => {
        try {
          const filterCriteria = row.path
            .replace("/SB/g", "Sponsored Brand")
            .replace("/SD/g", "Sponsored Display")
            .replace("/SP/g", "Sponsored Product");
          return `${filterCriteria}`;
        } catch {
          return "";
        }
      }}
      tableRowDescription={"Paths"}
      defaultSorted={[{ id: "impressions", desc: true }]}
      filterWidget={true}
      hideFilterToggleIcon={true}
      handleBulkChanges={() => {}}
      hideSearchFilter={false}
      hidePagination={false}
      {...filterProps}
      minRows={1}
      children={scopeDescription}
      selectionKey={"path"}
      ExpandedContent={({ row }: ExpandedContentProps) => {
        return (
          <PathToPurchaseExpander
            row={row?.original}
            manualCampaignLookup={manualCampaignLookup}
            autoCampaignLookup={autoCampaignLookup}
            audienceCampaignLookup={audienceCampaignLookup}
            contextualCampaignLookup={contextualCampaignLookup}
            isAdDataLoading={isAdDataLoading}
            campaignReportId={campaignReportId}
          />
        );
      }}
    />
  );
};

export default PathToPurchaseTable;
